<template>
  <div>
    <BackButton to="/settings" />
    <Toast />
    <ConfirmDialog :draggable="false" />
    <UsersTable
      title="Gestão de Usuários"
      :users="store.users"
      :show-add-button="!authStore.isCialLogin"
      :show-actions="true"
      :show-delete-button="!authStore.isCialLogin"
      @add-user="openModal(null)"
      @edit-user="openModal"
      @delete-user="confirmDeleteUser"
    />
    <Dialog v-model:visible="isModalOpen" :header="selectedUser ? 'Editar Usuário' : 'Adicionar Usuário'" :modal="true" class="w-full md:w-2/3 lg:w-1/2 xl:w-2/5" :draggable="false">
      <div class="flex flex-col gap-4 p-4">
        <div v-if="authStore.isCialLogin" class="flex flex-col gap-1">
          <div class="grid grid-cols-[8rem_1fr] items-baseline gap-3">
            <span class="text-sm font-semibold text-surface-500">Nome</span>
            <span class="text-base text-surface-900">{{ userForm.first_name || 'Não informado' }}</span>
          </div>
          <div class="grid grid-cols-[8rem_1fr] items-baseline gap-3">
            <span class="text-sm font-semibold text-surface-500">Sobrenome</span>
            <span class="text-base text-surface-900">{{ userForm.last_name || 'Não informado' }}</span>
          </div>
          <div class="grid grid-cols-[8rem_1fr] items-baseline gap-3">
            <span class="text-sm font-semibold text-surface-500">Email</span>
            <span class="break-all text-base text-surface-900">{{ userForm.email || 'Não informado' }}</span>
          </div>
        </div>
        <template v-else>
          <div class="flex flex-col">
            <label for="first_name" class="font-semibold mb-2">Nome</label>
            <InputText id="first_name" v-model.trim="userForm.first_name" required autofocus />
          </div>
          <div class="flex flex-col">
            <label for="last_name" class="font-semibold mb-2">Sobrenome</label>
            <InputText id="last_name" v-model.trim="userForm.last_name" required />
          </div>
          <div class="flex flex-col">
            <label for="email" class="font-semibold mb-2">Email</label>
            <InputText id="email" v-model.trim="userForm.email" required />
          </div>
        </template>
        <div class="flex flex-col" v-if="!selectedUser">
          <label for="password" class-="font-semibold mb-2">Senha</label>
          <InputText id="password" type="password" v-model="userForm.password" required />
        </div>
        <div v-if="authStore.isCialLogin" class="h-px" />
        <div class="flex items-center">
          <Checkbox id="is_account_admin" v-model="userForm.is_account_admin" :binary="true" />
          <label for="is_account_admin" class="ml-2">É Administrador</label>
        </div>
        <div class="flex flex-col mt-2">
          <label for="role" class="font-semibold mb-2">Papel no Sistema</label>
          <Select
            id="role"
            v-model="userForm.role_id"
            :options="store.roles"
            optionLabel="name"
            optionValue="id"
            placeholder="Selecione um papel"
            showClear
            filter
            filterPlaceholder="Buscar papel..."
            class="w-full"
          />
        </div>      </div>
      <template #footer>
        <Button label="Cancelar" @click="closeModal" class="p-button-text">
          <template #icon><span class="material-icons">close</span></template>
        </Button>
        <Button :label="selectedUser ? 'Atualizar' : 'Criar'" @click="saveUser">
          <template #icon><span class="material-icons">check</span></template>
        </Button>
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, reactive } from 'vue';
import { useAccountAdminUserStore } from '@/stores/accountAdminUserStore';
import { useAuthStore } from '@/stores/auth';
import { useConfirmDialog } from '@/composables/useConfirmDialog';
import UsersTable from '@/components/admin/UsersTable.vue';
import type { User, UserCreate, UserUpdate } from '@/types/user';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Checkbox from 'primevue/checkbox';
import Select from 'primevue/select';
import Button from 'primevue/button';
import ConfirmDialog from 'primevue/confirmdialog';
import Toast from 'primevue/toast';
import { useToast } from "primevue/usetoast";
import BackButton from '@/components/base/BackButton.vue';
import { getApiErrorCode } from '@/utils/apiErrorMessage';

const store = useAccountAdminUserStore();
const authStore = useAuthStore();
const { confirmDelete } = useConfirmDialog();
const toast = useToast();

const isModalOpen = ref(false);
const selectedUser = ref<User | null>(null);
const userForm = reactive<{
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  is_account_admin: boolean;
  role_id: string | null;
}>({
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  is_account_admin: false,
  role_id: null,
});

onMounted(() => {
  store.fetchUsers();
  store.fetchRoles();
});

const openModal = (user: User | null) => {
  if (!user && authStore.isCialLogin) return;

  selectedUser.value = user;
  if (user) {
    userForm.first_name = user.first_name;
    userForm.last_name = user.last_name;
    userForm.email = user.email;
    userForm.is_account_admin = user.is_account_admin;
    userForm.role_id = user.role?.id || null;
  } else {
    userForm.first_name = '';
    userForm.last_name = '';
    userForm.email = '';
    userForm.password = '';
    userForm.is_account_admin = false;
    userForm.role_id = null;
  }
  isModalOpen.value = true;
};

const closeModal = () => {
  isModalOpen.value = false;
  selectedUser.value = null;
};

const saveUser = async () => {
  try {
    if (selectedUser.value) {
      // Ensure is_admin is explicitly a boolean - backend expects 'is_admin'
      const userData: UserUpdate = {
        first_name: authStore.isCialLogin ? selectedUser.value.first_name : userForm.first_name?.trim(),
        last_name: authStore.isCialLogin ? selectedUser.value.last_name : userForm.last_name?.trim(),
        is_admin: !!userForm.is_account_admin, // Convert is_account_admin to is_admin for backend
        role_id: userForm.role_id || undefined,
      };
      await store.updateUser(selectedUser.value.id, userData);
      toast.add({severity:'success', summary: 'Sucesso', detail:'Usuário atualizado.', life: 3000});
    } else {
      if (authStore.isCialLogin) return;

      // Ensure is_admin is explicitly a boolean - backend expects 'is_admin' in UserCreate too
      const userData: UserCreate = {
        email: userForm.email!.trim(),
        first_name: userForm.first_name!.trim(),
        last_name: userForm.last_name!.trim(),
        password: userForm.password!,
        is_admin: !!userForm.is_account_admin, // Convert is_account_admin to is_admin for backend
        role_id: userForm.role_id || undefined,
      };
      await store.createUser(userData);
      toast.add({severity:'success', summary: 'Sucesso', detail:'Usuário criado.', life: 3000});
    }
    closeModal();
  } catch (error) {
    console.error('Failed to save user:', error);
    const detail = getApiErrorCode(error) === 'user_already_in_account'
      ? 'Este e-mail já pertence a um usuário desta conta.'
      : 'Não foi possível salvar o usuário. Tente novamente.';
    toast.add({severity:'error', summary: 'Erro', detail, life: 5000});
  }
};

const confirmDeleteUser = (user: User) => {
    if (authStore.isCialLogin) return;

    confirmDelete({
        message: `Tem certeza de que deseja excluir ${user.first_name} ${user.last_name}?`,
        header: 'Confirmar exclusão',
        icon: 'warning',
        accept: async () => {
            try {
              await store.deleteUser(user.id);
              toast.add({severity:'success', summary: 'Sucesso', detail:'Usuário excluído.', life: 3000});
            } catch (error) {
              console.error('Failed to delete user:', error);
              toast.add({severity:'error', summary: 'Erro', detail: 'Não foi possível excluir o usuário. Tente novamente.', life: 3000});
            }
        }
    });
};

</script>
